import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/cn';

/**
 * Guideline §12 — confirmation and unsaved-changes dialogs.
 * Traps focus, restores it on close, closes on Escape and on backdrop click.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'sm',
  /** Suppress backdrop dismissal where losing the dialog would lose work. */
  dismissible = true,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  dismissible?: boolean;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  /*
   * The handlers are read through refs so they are not part of the effect's
   * identity.
   *
   * `onClose` is written inline at every call site — `onClose={() => setOpen(false)}`
   * — so it is a different function on every render. With it in the dependency
   * list, typing one letter into a field re-rendered the parent, tore this
   * effect down (restoring focus to whatever opened the dialog) and set it up
   * again. The field lost focus after every keystroke, which is why a letter
   * needed a click first.
   */
  const onCloseRef = useRef(onClose);
  const dismissibleRef = useRef(dismissible);
  onCloseRef.current = onClose;
  dismissibleRef.current = dismissible;

  useEffect(() => {
    if (!open) return undefined;

    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && dismissibleRef.current) {
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab' || !panelRef.current) return;

      const focusable = panelRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    // Focus the panel itself so screen readers announce the dialog on open.
    window.setTimeout(() => panelRef.current?.focus(), 0);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = overflow;
      previouslyFocused.current?.focus();
    };
  }, [open]);

  if (!open) return null;

  const widths = { sm: 'max-w-md', md: 'max-w-xl', lg: 'max-w-3xl' } as const;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        aria-hidden
        onClick={dismissible ? onClose : undefined}
        className="absolute inset-0 animate-fade-in bg-midnight/40 backdrop-blur-[2px]"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        tabIndex={-1}
        /*
          Never taller than the screen.
          
          A dialog that grows with its content pushes its own footer off the
          bottom of the window, and a page behind a modal does not scroll — so
          a long one (editing a values section with six lines in it) became a
          dialog nobody could save or cancel. The body scrolls instead, and
          the title and the buttons stay where they are.
        */
        className={cn(
          'relative flex max-h-[calc(100vh-2rem)] w-full animate-fade-in flex-col rounded-xl border border-line bg-white shadow-modal focus:outline-none',
          widths[size],
        )}
      >
        <div className="shrink-0 px-6 pt-6">
          <h2 className="text-card-title text-ink">{title}</h2>
          {description && <p className="mt-2 text-sm leading-relaxed text-muted">{description}</p>}
        </div>
        {children && <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">{children}</div>}
        {footer && (
          <div className="shrink-0 flex items-center justify-end gap-3 border-t border-line px-6 py-4">{footer}</div>
        )}
        {/* Nothing under the title and no footer: the padding still has to
            close, or the dialog ends flush against its own text. */}
        {!children && !footer && <div className="h-6" />}
      </div>
    </div>,
    document.body,
  );
}
